import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';

import { naturalSearch } from '../../../actions/natural-search-actions';

import styles from './NaturalSearchInput.scss';

// Exported unconnected for tests.
export const NaturalSearchInputBase = ({
    loading,
    clarificationMessage,
    error,
    initialQuery,
    dispatchNaturalSearch,
}) => {
    const [query, setQuery] = useState(initialQuery || '');

    // initialQuery (state.naturalSearch.query) is hydrated from the URL in
    // SearchPage.componentDidMount, which runs after this input first renders.
    // Sync local state so a bookmarked NL query shows in the box on load.
    useEffect(() => {
        setQuery(initialQuery || '');
    }, [initialQuery]);

    const handleSubmit = (e) => {
        if (e) {
            e.preventDefault();
        }
        const trimmed = query.trim();
        if (!trimmed || loading) {
            return;
        }
        dispatchNaturalSearch(trimmed);
    };

    return (
        <div className={styles.naturalSearch} data-testid="search.nl-search.wrapper">
            <form
                className={styles.naturalSearchForm}
                onSubmit={handleSubmit}
                data-testid="search.nl-search.form"
            >
                <input
                    type="text"
                    className={styles.naturalSearchInput}
                    placeholder="Try: find 4.5 UTR juniors in Boston"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    disabled={loading}
                    aria-label="Natural-language search"
                    data-testid="search.nl-search.query-input"
                />
                <button
                    type="submit"
                    className={styles.naturalSearchSubmit}
                    disabled={loading || !query.trim()}
                    data-testid="search.nl-search.submit-btn"
                >
                    {loading ? (
                        <span data-testid="search.nl-search.submit-btn.loading">
                            Searching<span
                                className={styles.naturalSearchDots}
                                data-testid="search.nl-search.submit-btn.loading-dots"
                            >
                                <span className={styles.naturalSearchDot} data-testid="search.nl-search.submit-btn.loading-dot-1">.</span>
                                <span className={styles.naturalSearchDot} data-testid="search.nl-search.submit-btn.loading-dot-2">.</span>
                                <span className={styles.naturalSearchDot} data-testid="search.nl-search.submit-btn.loading-dot-3">.</span>
                                     </span>
                        </span>
                    ) : 'Search'}
                </button>
            </form>
            {clarificationMessage && (
                <div
                    className={styles.naturalSearchClarification}
                    role="status"
                    aria-live="polite"
                    data-testid="search.nl-search.clarification"
                >
                    {clarificationMessage}
                </div>
            )}
            {error && (
                <div
                    className={styles.naturalSearchError}
                    role="alert"
                    data-testid="search.nl-search.error"
                >
                    {error}
                </div>
            )}
        </div>
    );
};

NaturalSearchInputBase.propTypes = {
    loading: PropTypes.bool.isRequired,
    clarificationMessage: PropTypes.string,
    error: PropTypes.string,
    initialQuery: PropTypes.string,
    dispatchNaturalSearch: PropTypes.func.isRequired,
};

NaturalSearchInputBase.defaultProps = {
    clarificationMessage: null,
    error: null,
    initialQuery: '',
};

const mapStateToProps = (state) => ({
    loading: state.naturalSearch?.loading ?? false,
    clarificationMessage: state.naturalSearch?.clarificationMessage ?? null,
    error: state.naturalSearch?.error ?? null,
    initialQuery: state.naturalSearch?.query ?? '',
});

const mapDispatchToProps = (dispatch) => ({
    dispatchNaturalSearch: (query) => dispatch(naturalSearch(query)),
});

export default connect(mapStateToProps, mapDispatchToProps)(NaturalSearchInputBase);
